import React, { useRef, useCallback, useState, useEffect } from 'react'
import { Camera, Download } from 'lucide-react'
import BaseNoteViewer from './BaseNoteViewer'
import FlowchartCanvas from '../common/FlowchartCanvas'
import toast from 'react-hot-toast'

/**
 * FlowchartNoteViewer Component
 * 
 * Specialized viewer for flowchart notes with read-only rendering and capture functionality.
 * Provides pan/zoom controls and image capture/download capabilities.
 */
const FlowchartNoteViewer = ({ note, ...props }) => {
  const flowchartRef = useRef(null)
  const containerRef = useRef(null)
  const [isCapturing, setIsCapturing] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isFlowchartReady, setIsFlowchartReady] = useState(false)
  
  /**
   * Enable flowchart rendering after component mounts
   */
  useEffect(() => {
    // Simple approach: enable flowchart after a short delay
    const timer = setTimeout(() => {
      setIsFlowchartReady(true)
    }, 200)
    
    return () => clearTimeout(timer)
  }, [note])
  
  /**
   * Capture flowchart as image and copy to clipboard
   */
  const handleCapture = useCallback(async () => {
    if (!flowchartRef.current) {
      toast.error('Flowchart not available for capture')
      return
    }
    
    setIsCapturing(true)
    
    try {
      // Export as image (PNG format)
      const imageDataUrl = await flowchartRef.current.exportAsImage()
      
      if (!imageDataUrl) {
        toast.error('Failed to capture flowchart')
        return
      }
      
      // Convert PNG to JPG format
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      await new Promise((resolve, reject) => {
        img.onload = () => {
          canvas.width = img.width
          canvas.height = img.height
          
          // Fill with white background
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          
          // Draw the image
          ctx.drawImage(img, 0, 0)
          
          // Convert to JPG
          const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9)
          resolve(jpgDataUrl)
        }
        img.onerror = reject
        img.src = imageDataUrl
      })
      
      // Convert JPG data URL to blob
      const response = await fetch(canvas.toDataURL('image/jpeg', 0.9))
      const blob = await response.blob()
      
      // Try to copy to clipboard
      try {
        await navigator.clipboard.write([
          new ClipboardItem({
            [blob.type]: blob
          })
        ])
        
        toast.success('Flowchart captured and copied to clipboard!', {
          duration: 3000,
          style: {
            background: 'rgba(16, 185, 129, 0.5)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            color: 'white',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(16, 185, 129, 0.3)'
          }
        })
      } catch (clipboardError) {
        // Fallback: download the image
        console.warn('Clipboard API failed, falling back to download:', clipboardError)
        await handleDownloadFallback(blob, 'captured')
      }
      
    } catch (error) {
      console.error('Failed to capture flowchart:', error)
      toast.error('Failed to capture flowchart')
    } finally {
      setIsCapturing(false)
    }
  }, [])
  
  /**
   * Download flowchart as image
   */
  const handleDownload = useCallback(async () => {
    if (!flowchartRef.current) {
      toast.error('Flowchart not available for download')
      return
    }
    
    setIsDownloading(true)
    
    try {
      // Export as image (PNG format)
      const imageDataUrl = await flowchartRef.current.exportAsImage()
      
      if (!imageDataUrl) {
        toast.error('Failed to generate flowchart image')
        return
      }
      
      // Convert PNG to JPG format
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      const img = new Image()
      
      await new Promise((resolve, reject) => {
        img.onload = () => {
          canvas.width = img.width
          canvas.height = img.height
          
          // Fill with white background
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          
          // Draw the image
          ctx.drawImage(img, 0, 0)
          
          resolve()
        }
        img.onerror = reject
        img.src = imageDataUrl
      })
      
      // Convert to JPG blob
      const jpgDataUrl = canvas.toDataURL('image/jpeg', 0.9)
      const response = await fetch(jpgDataUrl)
      const blob = await response.blob()
      
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${note.title || 'flowchart'}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Flowchart downloaded successfully!', {
        duration: 2000,
        style: {
          background: 'rgba(16, 185, 129, 0.5)',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          color: 'white',
          borderRadius: '12px',
          padding: '12px 16px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          border: '1px solid rgba(16, 185, 129, 0.3)'
        }
      })
      
    } catch (error) {
      console.error('Failed to download flowchart:', error)
      toast.error('Failed to download flowchart')
    } finally {
      setIsDownloading(false)
    }
  }, [note.title])
  
  /**
   * Fallback download function for clipboard failures
   */
  const handleDownloadFallback = useCallback(async (blob, suffix = '') => {
    try {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${note.title || 'flowchart'}${suffix ? `_${suffix}` : ''}.jpg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Flowchart downloaded (clipboard not available)', {
        duration: 3000
      })
    } catch (error) {
      console.error('Failed to download flowchart fallback:', error)
      toast.error('Failed to download flowchart')
    }
  }, [note.title])
  
  /**
   * Custom actions for flowchart viewer - icon-only buttons
   */
  const flowchartActions = (
    <div className="flex items-center space-x-2">
      <button
        onClick={handleCapture}
        disabled={isCapturing}
        className="p-2 rounded-full text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Capture flowchart to clipboard"
      >
        <Camera className="h-5 w-5" />
      </button>
      
      <button
        onClick={handleDownload}
        disabled={isDownloading}
        className="p-2 rounded-full text-gray-400 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        title="Download flowchart as image"
      >
        <Download className="h-5 w-5" />
      </button>
    </div>
  )
  
  /**
   * Render flowchart content
   */
  const renderFlowchartContent = () => {
    if (!note?.flowchartData) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-500 dark:text-gray-400 mb-4">
              No flowchart data available
            </p>
            <p className="text-sm text-gray-400 dark:text-gray-500">
              This flowchart note appears to be empty or corrupted.
            </p>
          </div>
        </div>
      )
    }
    
    return (
      <div 
        ref={containerRef}
        className="h-full w-full relative bg-gray-50 dark:bg-gray-800" 
        style={{ 
          height: '100%', 
          minHeight: '700px',
          width: '100%',
          position: 'relative'
        }}
      >
        {/* Flowchart Canvas - Read Only */}
        {isFlowchartReady ? (
          <div 
            style={{ 
              width: '100%', 
              height: '100%', 
              minHeight: '600px',
              position: 'relative'
            }}
          >
            <FlowchartCanvas
              ref={flowchartRef}
              initialData={note.flowchartData}
              readOnly={true}
              className="w-full h-full"
              style={{ 
                width: '100%', 
                height: '100%', 
                minHeight: '500px',
                position: 'absolute',
                top: 0,
                left: 0
              }}
            />
          </div>
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-gray-500 dark:text-gray-400">Loading flowchart...</p>
            </div>
          </div>
        )}
        
      </div>
    )
  }
  
  return (
    <BaseNoteViewer 
      note={note} 
      {...props}
      customActions={flowchartActions}
    >
      {renderFlowchartContent()}
    </BaseNoteViewer>
  )
}

FlowchartNoteViewer.displayName = 'FlowchartNoteViewer'

export default FlowchartNoteViewer
